import type { LeaderMirrorPayload } from "@cold-case/shared/player-follow";
import type { Socket } from "socket.io-client";

let sessionId: string | null = null;
let selfUserId: string | null = null;
let socket: Socket | null = null;
let followerCount = 0;
let followerUserIds: string[] = [];
let unbindFollowersChanged: (() => void) | null = null;
let onFollowersAttached: (() => void) | null = null;

/** Fires when the first follower attaches (leader should push overlay/journal mirror). */
export function setOnFollowersAttached(cb: (() => void) | null): void {
  onFollowersAttached = cb;
}

function bindFollowersChanged(sock: Socket | null, getSessionId: () => string, getSelfUserId: () => string): void {
  unbindFollowersChanged?.();
  unbindFollowersChanged = null;
  if (!sock) return;

  const onChanged = (payload: {
    sessionId: string;
    leaderUserId: string;
    followerUserIds: string[];
  }) => {
    if (payload.sessionId !== getSessionId()) return;
    if (payload.leaderUserId !== getSelfUserId()) return;
    const hadFollowers = followerCount > 0;
    followerUserIds = [...payload.followerUserIds];
    followerCount = followerUserIds.length;
    if (!hadFollowers && followerCount > 0) {
      onFollowersAttached?.();
    }
  };

  sock.on("game:followers_changed", onChanged);
  unbindFollowersChanged = () => {
    sock.off("game:followers_changed", onChanged);
  };
}

export function initLeaderFollowMirror(opts: {
  getSessionId: () => string;
  getSelfUserId: () => string;
  getSocket: () => Socket | null;
}): void {
  sessionId = opts.getSessionId();
  selfUserId = opts.getSelfUserId();
  socket = opts.getSocket();
  followerCount = 0;
  followerUserIds = [];
  bindFollowersChanged(socket, opts.getSessionId, opts.getSelfUserId);
}

export function setLeaderFollowMirrorSession(
  id: string | null,
  sock: Socket | null,
  uid: string | null
): void {
  sessionId = id;
  socket = sock;
  selfUserId = uid;
  followerCount = 0;
  followerUserIds = [];
  if (id && sock && uid) {
    bindFollowersChanged(sock, () => id, () => uid);
  } else {
    unbindFollowersChanged?.();
    unbindFollowersChanged = null;
  }
}

export function leaderHasFollowers(): boolean {
  return followerCount > 0;
}

export function getLeaderFollowerUserIds(): readonly string[] {
  return followerUserIds;
}

export function emitLeaderMirror(mirror: LeaderMirrorPayload): void {
  if (!leaderHasFollowers()) return;
  const sid = sessionId;
  const sock = socket;
  if (!sid || !sock?.connected) return;
  sock.emit("game:leader_mirror", { sessionId: sid, mirror });
}

export function disposeLeaderFollowMirror(): void {
  unbindFollowersChanged?.();
  unbindFollowersChanged = null;
  onFollowersAttached = null;
  sessionId = null;
  selfUserId = null;
  socket = null;
  followerCount = 0;
  followerUserIds = [];
}
